import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Loadable } from '@/components/loaders';
import { useWorkspace } from '@/features/workspaces/hooks';
import { useDelayedBoolean } from '@/hooks/useDelayedBoolean';
import { useGetCampaignReservationQuery } from '@/services/endpoints/credits';
import { formatCreditDateTime, formatCredits } from '../utils/formatCredits';
import { isCreditsFeatureUnavailable } from '../utils/isCreditsFeatureUnavailable';

const MINIMUM_LOADING_DURATION = 300;

type CampaignsCreditReservationPanelProps = {
  campaignId: string;
};

export const CampaignsCreditReservationPanel: FC<CampaignsCreditReservationPanelProps> = ({ campaignId }) => {
  const { t, i18n } = useTranslation('campaign');
  const workspace = useWorkspace();

  const { data, isLoading, isFetching, isError, error, isUninitialized } = useGetCampaignReservationQuery(
    { workspaceId: workspace.id, campaignId },
    { skip: !workspace.id || !campaignId }
  );

  const isLoadingDelayed = useDelayedBoolean(isLoading || isFetching, MINIMUM_LOADING_DURATION);

  if (isCreditsFeatureUnavailable(error)) {
    return null;
  }

  const isNotFound = isError && error && 'status' in error && error.status === 404;
  const showLoading = isUninitialized || isLoading || isLoadingDelayed;

  if (isError && !isNotFound) {
    return null;
  }

  const amount = data?.amount ?? 0;
  const consumed = data?.consumed ?? 0;
  const pct = amount > 0 ? Math.min(100, Math.round((consumed / amount) * 100)) : 0;
  const released = Math.max(0, amount - consumed);
  const isClosed = data?.status === 'SETTLED' || data?.status === 'EXPIRED';

  return (
    <Loadable isLoading={showLoading}>
      {isNotFound || !data ? (
        <section className='rounded-2xl border border-secondary-border bg-white-100 p-5'>
          <p className='text-xs font-extrabold uppercase tracking-[0.05em] text-secondary-600'>{t('credits.reservation.title')}</p>
          <p className='mt-2 text-sm text-secondary-600'>{t('credits.reservation.empty')}</p>
        </section>
      ) : (
        <section className='rounded-2xl border border-secondary-border bg-white-100 p-5'>
          <div className='flex flex-wrap items-center justify-between gap-3'>
            <p className='text-xs font-extrabold uppercase tracking-[0.05em] text-secondary-600'>{t('credits.reservation.title')}</p>
            <span className='rounded-full bg-secondary-background px-3 py-1 text-[11px] font-extrabold uppercase tracking-[0.06em] text-secondary-700'>
              {data.status}
            </span>
          </div>

          <div className='mt-4 grid gap-3 sm:grid-cols-3'>
            <div>
              <p className='text-xs text-secondary-500'>{t('credits.reservation.reserved')}</p>
              <p className='text-lg font-bold text-slate-900'>{formatCredits(amount, i18n.language)}</p>
            </div>
            <div>
              <p className='text-xs text-secondary-500'>{t('credits.reservation.consumed')}</p>
              <p className='text-lg font-bold text-slate-900'>{formatCredits(consumed, i18n.language)}</p>
            </div>
            <div>
              <p className='text-xs text-secondary-500'>
                {isClosed ? t('credits.reservation.released') : t('credits.reservation.onHold')}
              </p>
              <p className='text-lg font-bold text-slate-900'>{formatCredits(released, i18n.language)}</p>
            </div>
          </div>

          <div className='mt-4 h-2.5 overflow-hidden rounded-full bg-secondary-background'>
            <div className='h-full rounded-full bg-brand-600' style={{ width: `${pct}%` }} />
          </div>
          <p className='mt-1.5 text-xs font-semibold text-secondary-600'>{t('credits.reservation.pctConsumed', { pct })}</p>

          <p className='mt-4 text-sm text-secondary-600'>{t('credits.reservation.note')}</p>
          <div className='mt-3 space-y-1 text-xs text-secondary-500'>
            <p>{t('credits.reservation.createdAt', { date: formatCreditDateTime(data.createdAt, i18n.language) })}</p>
            <p>{t('credits.reservation.expiresAt', { date: formatCreditDateTime(data.expiresAt, i18n.language) })}</p>
          </div>
        </section>
      )}
    </Loadable>
  );
};
